import React, { useMemo } from 'react';
import { WorldSection } from './WorldPageHeader';
import { StatusBadge } from './VerificationBits';
import { characterClasses } from '../../data/guide/classes';
import { publishedQuestIds, questExpectations, questTitle } from '../../data/guide/quests';
import { buildQuestView, stageName, variantFor } from '../../utils/questView';
import type { WorldData } from '../../types/world';
const th = 'px-3 py-2.5 font-semibold';
const td = 'px-3 py-2.5 align-top';

/** Panel técnico de los siete archivos de misiones: requisitos, clases, variantes, drops y referencias pendientes. */
export function QuestDataCheck({
  w
}: {
  w: WorldData;
}) {
  const q = w.quests;
  const data = useMemo(() => {
    const quests = publishedQuestIds.map(id => {
      const v = buildQuestView(w, id);
      const normal = q.rules.filter(r => r.questId === id && r.state === 0);
      const mins = Array.from(new Set(normal.map(r => r.levelMin)));
      const exp = questExpectations[id];
      return {
        id,
        v,
        mins,
        exp
      };
    });
    const items = q.objectives.filter(o => o.typeName === 'item' && o.itemId).map(o => ({
      o,
      res: variantFor(w, o.itemId!, o.itemLevel),
      drop: q.dropRules.find(d => d.objectiveId === o.id)
    }));
    const drops = q.dropRules.map(d => {
      const targets = q.dropTargets.get(d.id) ?? [];
      const missingMon = targets.filter(t => t.monsterId !== undefined && !w.monsters.has(t.monsterId)).length;
      const missingMap = targets.filter(t => t.mapId !== undefined && !w.maps.has(t.mapId)).length;
      const withSpawns = targets.filter(t => t.monsterId !== undefined && (w.idx.spawnsByMonster.get(t.monsterId) ?? []).some(s => t.mapId === undefined || s.mapId === t.mapId)).length;
      const routeRows = w.routes.filter(r => r.kind === 'quest' && r.questDropRuleId === d.id).reduce((n, r) => n + (w.idx.routeTargetsByRoute.get(r.id)?.length ?? 0), 0);
      return {
        d,
        targets: targets.length,
        missingMon,
        missingMap,
        withSpawns,
        routeRows
      };
    });
    const unpublished = Array.from(new Set(q.rules.map(r => r.questId))).filter(id => !publishedQuestIds.includes(id)).sort((a, b) => a - b).map(id => {
      const npcIds = Array.from(new Set(q.rules.filter(r => r.questId === id).map(r => r.npcMonsterId).filter((n): n is number => n !== undefined)));
      const objs = q.objectives.filter(o => o.questId === id);
      const refs = [...npcIds.map(n => `NPC ${n}: ${w.monsters.get(n)?.name ?? 'no está en monsters.csv'}`), ...objs.map(o => {
        const mid = o.monsterId ?? o.configuredMonsterId;
        return `${o.typeName ?? 'objetivo'} ${o.monsterId !== undefined ? 'monster_id' : 'configured_monster_id'}=${mid ?? '—'} × ${o.quantity ?? '—'}: ${mid !== undefined ? w.monsters.get(mid)?.name ?? 'no está en monsters.csv' : 'sin monstruo'}`;
      })];
      return {
        id,
        refs
      };
    });
    return {
      quests,
      items,
      drops,
      unpublished
    };
  }, [w, q]);
  const loaded = q.rules.length > 0;
  return <WorldSection id="misiones" title="Misiones: archivos exportados" data-id="element-1766" data-mp="xyb5hvz01o29">
      {!loaded ? <p className="text-sm text-red-200" data-id="element-1767" data-mp="sw29sruqs7wg">No se cargó ninguna regla de misión. Revisa los errores de quest_rules.csv en “Archivos”.</p> : <div className="space-y-6" data-id="element-1768" data-mp="jx8hhdog23op">
          <p className="text-sm text-gray-400" data-id="element-1769" data-mp="jy0b4iyhdrcc">
            {q.quests.size} misiones · {q.rules.length} reglas · {q.objectives.length} objetivos · {q.dropRules.length} reglas de drop ·{' '}
            {Array.from(q.dropTargets.values()).reduce((n, l) => n + l.length, 0)} objetivos de drop. Confirmado por configuración exportada; nada probado dentro del juego.
          </p>

          <div className="overflow-x-auto rounded-2xl border border-white/10" data-id="element-1770" data-mp="12wcpqk85vtfb">
            <table className="w-full min-w-[640px] text-left text-sm" data-id="element-1771" data-mp="1zdphzahlrtrl">
              <thead className="bg-white/[0.03] text-xs text-gray-400" data-id="element-1772" data-mp="1zftsiba0br0u">
                <tr data-id="element-1773" data-mp="1um7eofs3xh3g"><th className={th} data-id="element-1774" data-mp="1um7g8k7r9p5k">Misión</th><th className={th} data-id="element-1775" data-mp="1ukwdmnq1qng1">Nivel mín. (reglas normal)</th><th className={th} data-id="element-1776" data-mp="1f4ptenhxpbvm">Zen (normal / cancelada)</th><th className={th} data-id="element-1777" data-mp="1f72db6795wbb">Referencia staff</th><th className={th} data-id="element-1778" data-mp="1k4lr6501g72q">Estado</th></tr>
              </thead>
              <tbody className="divide-y divide-white/5" data-id="element-1779" data-mp="164ax6t3fmcdc">
                {data.quests.map(({
              id,
              v,
              mins,
              exp
            }) => {
              const ok = !!v && mins.length === 1 && mins[0] === exp.levelMin && v.zenConsistent && v.zen === exp.zen;
              return <tr key={id} data-id="element-1780" data-mp="1xw8x6wjs8typ">
                      <td className={`${td} text-gray-200`} data-id="element-1781" data-mp="22uah1jp18xb8">{id} · {questTitle(id)}</td>
                      <td className={`${td} font-mono text-[12px] text-white`} data-id="element-1782" data-mp="22wem44yo42d3">{mins.join(' / ') || '—'}</td>
                      <td className={`${td} font-mono text-[12px] text-white`} data-id="element-1783" data-mp="1t4qtw1uaehak">{v?.zen ?? (v?.zenConsistent === false ? 'distintos' : '—')} <span className="text-gray-500" data-id="element-1784" data-mp="2gttbxasu2c5l">({v?.zenStates.join(', ') || '—'})</span></td>
                      <td className={`${td} font-mono text-[12px] text-gray-400`} data-id="element-1785" data-mp="2lll23k82ufv7">{exp.levelMin} · {exp.zen}</td>
                      <td className={td} data-id="element-1786" data-mp="a514ad2ejxxl"><StatusBadge status={ok ? 'match' : 'mismatch'} label={ok ? 'Coincide' : 'Diferencia'} data-id="element-1787" data-mp="a4hwhp6gupxa" /></td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-white/10" data-id="element-1788" data-mp="jxqbgvqx6pn4">
            <table className="w-full min-w-[760px] text-left text-sm" data-id="element-1789" data-mp="exkr80mzc5f2">
              <thead className="bg-white/[0.03] text-xs text-gray-400" data-id="element-1790" data-mp="3ndns9njl6vuf">
                <tr data-id="element-1791" data-mp="3ie1b530n0vgc"><th className={th} data-id="element-1792" data-mp="3ie19kykzone8">Misión</th>{characterClasses.map(c => <th key={c.id} className={th} data-id="element-1793" data-mp="3ifm8t1i90uif">{c.id.toUpperCase()}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-white/5" data-id="element-1794" data-mp="13be1ak9b51e">
                {publishedQuestIds.map(id => <tr key={id} data-id="element-1795" data-mp="3se1yd01mnvpt">
                    <td className={`${td} text-gray-200`} data-id="element-1796" data-mp="1cjrwzdv0s3t0">{id}</td>
                    {characterClasses.map(c => {
                const v = buildQuestView(w, id, c);
                const s = v?.starts[0];
                return <td key={c.id} className={`${td} text-xs`} data-id="element-1797" data-mp="1eqr6q22eeufq">
                          {v?.eligible && s ? <span className="text-gray-200" data-id="element-1798" data-mp="1omui7yj545fc">
                              {stageName(c, s.classes[0]?.changeUpPlusOne) ?? '?'}
                              <span className="block text-gray-500" data-id="element-1799" data-mp="1tkmc2y3ci1m6">previa: {s.rule.requiredQuestId ?? '—'}</span>
                              <span className="block text-gray-500" data-id="element-1800" data-mp="vzqc1q8b9l1z">{v.items.length} ítems · {v.kills.length} bajas</span>
                            </span> : <span className="text-gray-600" data-id="element-1801" data-mp="15vl050vb08c5">No aplica</span>}
                        </td>;
              })}
                  </tr>)}
              </tbody>
            </table>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-white/10" data-id="element-1802" data-mp="i68ugaz6qiel">
            <table className="w-full min-w-[640px] text-left text-sm" data-id="element-1803" data-mp="2vh1vp007jpt6">
              <thead className="bg-white/[0.03] text-xs text-gray-400" data-id="element-1804" data-mp="2vexqmeqkokrb">
                <tr data-id="element-1805" data-mp="2rgrndgoy0nb5"><th className={th} data-id="element-1806" data-mp="2rfydi6uothos">Objetivo</th><th className={th} data-id="element-1807" data-mp="2rdlz23o51pgh">item_id @ item_level</th><th className={th} data-id="element-1808" data-mp="2mfkf7giw1m3y">Nombre resuelto</th><th className={th} data-id="element-1809" data-mp="2meitmi48xh7z">Cantidad</th><th className={th} data-id="element-1810" data-mp="2hh95r6wi9dm9">Regla de drop</th></tr>
              </thead>
              <tbody className="divide-y divide-white/5" data-id="element-1811" data-mp="2hjd7bi6xm0jd">
                {data.items.map(({
              o,
              res,
              drop
            }) => <tr key={o.id} data-id="element-1812" data-mp="27rhasbkhuih9">
                    <td className={`${td} text-gray-400`} data-id="element-1813" data-mp="27qeji2j9ee0t">{o.id} · misión {o.questId}</td>
                    <td className={`${td} font-mono text-[12px] text-white`} data-id="element-1814" data-mp="23p2qro539coe">{o.itemId}@{o.itemLevel ?? '—'}</td>
                    <td className={td} data-id="element-1815" data-mp="1yru53dkqeqie">
                      <span className="text-gray-200" data-id="element-1816" data-mp="1yooj477eg4qw">{res.name}</span>
                      <span className="block text-xs text-gray-500" data-id="element-1817" data-mp="1tr6gsznw0nrv">{res.fromVariant ? `variante ${res.id}` : 'sin variante en el catálogo: nombre base'}</span>
                    </td>
                    <td className={`${td} text-gray-200`} data-id="element-1818" data-mp="3dnu5t3vbfa3h">{o.quantity ?? '—'}</td>
                    <td className={`${td} text-xs text-gray-400`} data-id="element-1819" data-mp="3how2bcxul81r">{drop ? drop.id : <StatusBadge status="mismatch" label="Sin regla" data-id="element-1820" data-mp="3hm1lrwhyg7dm" />}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-white/10" data-id="element-1821" data-mp="38m4r9e8vwean">
            <table className="w-full min-w-[640px] text-left text-sm" data-id="element-1822" data-mp="2txqof3bgwpu3">
              <thead className="bg-white/[0.03] text-xs text-gray-400" data-id="element-1823" data-mp="2yq1pwaluwjor">
                <tr data-id="element-1824" data-mp="2yrcuudr1fxhg"><th className={th} data-id="element-1825" data-mp="2yrcq60g3f9b4">Regla de drop</th><th className={th} data-id="element-1826" data-mp="2kywmqyja599o">Selector</th><th className={th} data-id="element-1827" data-mp="2kzojicom5d1j">Tirada</th><th className={th} data-id="element-1828" data-mp="2kwaf6ywe2trg">Objetivos</th><th className={th} data-id="element-1829" data-mp="2pwex8tcy3wjs">Con coordenadas</th><th className={th} data-id="element-1830" data-mp="2pxq3cztv5ge2">Referencias rotas</th></tr>
              </thead>
              <tbody className="divide-y divide-white/5" data-id="element-1831" data-mp="2b18qg460ztpe">
                {data.drops.map(r => <tr key={r.d.id} data-id="element-1832" data-mp="2fxpfr1oyoe42">
                    <td className={`${td} text-gray-200`} data-id="element-1833" data-mp="2g0v1q82amzvk">{r.d.id} · {r.d.itemId}@{r.d.itemLevel ?? '—'}</td>
                    <td className={`${td} text-xs text-gray-400`} data-id="element-1834" data-mp="1sbn604yrh8bw">{r.d.selector}{r.d.monsterLevelMin !== undefined ? ` ${r.d.monsterLevelMin}–${r.d.monsterLevelMax}` : ''}{r.d.monsterId !== undefined ? ` · clase ${r.d.monsterId}` : ''}</td>
                    <td className={`${td} font-mono text-[12px] text-white`} data-id="element-1835" data-mp="21ba68nfr883d">{r.d.rollNumerator}/{r.d.rollDenominator}</td>
                    <td className={`${td} text-gray-200`} data-id="element-1836" data-mp="2fajk3gghmq46">{r.targets}<span className="block text-xs text-gray-500" data-id="element-1837" data-mp="2barencme5031">route_targets: {r.routeRows} filas (sin duplicar)</span></td>
                    <td className={`${td} text-gray-200`} data-id="element-1838" data-mp="2p0usbf77flgv">{r.withSpawns}</td>
                    <td className={td} data-id="element-1839" data-mp="2k2b5lckse5ng">{r.missingMon + r.missingMap ? <StatusBadge status="mismatch" label={`${r.missingMon} monstruo · ${r.missingMap} mapa`} data-id="element-1840" data-mp="2k3l4g3zgrhv5" /> : <StatusBadge status="match" label="Ninguna" data-id="element-1841" data-mp="2z0lqbquz4ilp" />}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <div data-id="element-1842" data-mp="1nhmt6ipitx8z">
            <p className="text-sm font-semibold text-white" data-id="element-1843" data-mp="1ni4z671zjpu3">Relaciones entre archivos de misiones</p>
            <ul className="mt-2 space-y-1 text-sm" data-id="element-1844" data-mp="1ipv46cm0jzky">
              {q.orphans.map(o => <li key={o.label} className="flex flex-wrap items-center gap-2 text-gray-300" data-id="element-1845" data-mp="1dsd3f9i5gqo1">
                  <StatusBadge status={o.ids.length ? 'mismatch' : 'match'} label={o.ids.length ? `${o.ids.length} sin resolver` : 'OK'} data-id="element-1846" data-mp="19qzz53upgvj8" />
                  {o.label}
                  {o.ids.length > 0 && <span className="font-mono text-[11px] text-gray-500" data-id="element-1847" data-mp="21gwbpp1793g4">{o.ids.slice(0, 8).join(', ')}</span>}
                </li>)}
            </ul>
          </div>

          <div data-id="element-1848" data-mp="1sgpze6h8m9ij">
            <p className="text-sm font-semibold text-white" data-id="element-1849" data-mp="1sh96st99hfib">Misiones no publicadas (referencias pendientes)</p>
            <p className="mt-1 text-xs text-gray-400" data-id="element-1850" data-mp="m1f9k241r00z">No se muestran como evoluciones disponibles hasta confirmar su funcionamiento.</p>
            <ul className="mt-2 space-y-2 text-sm" data-id="element-1851" data-mp="h3dt7oy09et7">
              {data.unpublished.map(u => <li key={u.id} className="rounded-xl border border-white/10 p-3" data-id="element-1852" data-mp="2smjr6ao8r9z0">
                  <span className="font-semibold text-white" data-id="element-1853" data-mp="2xo9641el47sd">Misión {u.id}</span>
                  <ul className="mt-1 space-y-0.5 font-mono text-[11px] text-gray-400" data-id="element-1854" data-mp="31oj7t1vcgc1f">{u.refs.map(r => <li key={r} data-id="element-1855" data-mp="36gb3trx9r9yy">{r}</li>)}</ul>
                </li>)}
            </ul>
          </div>
        </div>}
    </WorldSection>;
}